import { createSelector } from '@atlassian/eoc-rss-helpers';
import { NavigationMode } from '@atlassian/eoc-schedule-common';

import { type RotationBase } from '../../../common/types';
import { isNullOrUndefined } from '../../../utils/validators';
import { type initialState } from '../state';
import type { ScheduleCalendarData, ScheduleStore, ScheduleTimelineData } from '../state/types';

// NOTE: Loadings, Rotations, Data, Epochs might be selected by single selector
const getEpoch = (state: ScheduleStore) => {
	if (state.selectedView === NavigationMode.Timeline) {
		const {
			startEpoch = 0,
			startOffset = 0,
			endEpoch = 0,
			endOffset = 0,
			nowEpoch = 0,
			nowOffset = 0,
		} = state.timelineData || {};

		return {
			startEpoch,
			startOffset,
			endEpoch,
			endOffset,
			nowEpoch,
			nowOffset,
		};
	}

	if (state.selectedView === NavigationMode.Calendar) {
		const {
			startEpoch = 0,
			startOffset = 0,
			endEpoch = 0,
			endOffset = 0,
			nowEpoch = 0,
			nowOffset = 0,
		} = state.calendarData || {};

		return {
			startEpoch,
			startOffset,
			endEpoch,
			endOffset,
			nowEpoch,
			nowOffset,
		};
	}

	return {
		startEpoch: 0,
		endEpoch: 0,
		startOffset: 0,
		endOffset: 0,
		nowEpoch: 0,
		nowOffset: 0,
	};
};

// NOTE: should we filter past rotations
const getRotations = createSelector<
	typeof initialState,
	// Hook input
	void,
	// Selector output
	RotationBase[] | undefined,
	// Selector input
	NavigationMode,
	ScheduleTimelineData | undefined,
	ScheduleCalendarData | undefined
>(
	(state) => state.selectedView,
	(state) => state.timelineData,
	(state) => state.calendarData,
	(
		view: NavigationMode,
		timeline: ScheduleTimelineData | undefined,
		calendar: ScheduleCalendarData | undefined,
	) => {
		if (view === NavigationMode.Timeline) {
			if (isNullOrUndefined(timeline)) {
				return undefined;
			}

			return timeline!.defaultLayers.map(({ id, name }) => ({
				id,
				name,
			})) as RotationBase[];
		}

		if (view === NavigationMode.Calendar) {
			if (isNullOrUndefined(calendar)) {
				return undefined;
			}

			const allRotations = new Map<string, string>();

			for (const week of calendar!.baseWeeks) {
				for (const rotation of week.rotations) {
					allRotations.set(rotation.id, rotation.name);
				}
			}

			return Array.from(allRotations.entries(), ([id, name]) => ({
				id,
				name,
			})) as RotationBase[];
		}
	},
);

const getSchedule = (state: ScheduleStore) => {
	return state.schedule;
};

const getRotation = (state: ScheduleStore) => {
	return state.rotation;
};

const getHasDeleted = (state: ScheduleStore) => {
	let hasDeletedResponders = false;

	if (state.selectedView === NavigationMode.Timeline) {
		hasDeletedResponders = state?.timelineData?.hasDeletedResponders ?? false;
	}

	if (state.selectedView === NavigationMode.Calendar) {
		hasDeletedResponders = state?.calendarData?.hasDeletedResponders ?? false;
	}

	return hasDeletedResponders;
};

const getLoading = (state: ScheduleStore) => {
	let isInitialLoading = false;
	const isLoading = state.getTimelineLoading;

	if (state.selectedView === NavigationMode.Timeline) {
		isInitialLoading = isNullOrUndefined(state.timelineData) && state.getTimelineLoading;
	}

	if (state.selectedView === NavigationMode.Calendar) {
		isInitialLoading = isNullOrUndefined(state.calendarData) && state.getTimelineLoading;
	}

	return {
		isLoading,
		isInitialLoading,
		isSubsequentLoading: !isInitialLoading && isLoading,
	};
};

export const selectors = {
	getSchedule,
	getEpoch,
	getRotations,
	getLoading,
	getRotation,
	getHasDeleted,
};
